// Central mock data for the "Managers" (Manager-of-Managers) hub — FE-first,
// NOT wired to the backend. A MoM owns several communities that CMs run; he
// takes a cut of each community's pool (invisible to the CM). ALL split
// percentages / amounts here are ILLUSTRATIVE placeholders — in production they
// come from the revshare system, not the FE. Income is shown for LAST month,
// since suppliers settle after the month ends.

// Item types shown in the "items by type" column, in order. Driven by data.
export const ITEM_TYPES = ["services", "products", "foods"];

// Mock category names per item type — shown in each community chip's tooltip
// (like the My Suppliers "Categories in" tooltip).
export const ITEM_CATEGORIES = {
  services: ["ניקיון", "אינסטלציה", "חשמל", "גינון", "שיפוצים", "הובלות"],
  products: ["כלי בית", "טקסטיל", "אלקטרוניקה"],
  foods: ["מאפים", "קייטרינג", "קפה"],
};

export const managersHubMock = {
  currencySymbol: "₪",
  momName: { en: "Roei Dahan", he: "רועי דהן" },

  // Earnings & payouts — the MoM's own cut, accrued and paid out over time.
  // Mirrors the supplier dashboard's earnings card. Amounts ILLUSTRATIVE.
  earnings: {
    currentBalance: 16200, // accrued this-period cut, available to withdraw
    allTime: 184600,
    thisMonth: 16200,
    nextPayout: { en: "Sep 1, 2026", he: "1 בספט׳ 2026" },
    payouts: [
      { period: { en: "Jul 2026", he: "יולי 2026" }, amount: 16200, status: "pending", date: null },
      { period: { en: "Jun 2026", he: "יוני 2026" }, amount: 15800, status: "paid", date: { en: "Jul 1, 2026", he: "1 ביולי 2026" } },
      { period: { en: "May 2026", he: "מאי 2026" }, amount: 14950, status: "paid", date: { en: "Jun 1, 2026", he: "1 ביוני 2026" } },
      { period: { en: "Apr 2026", he: "אפר׳ 2026" }, amount: 13400, status: "paid", date: { en: "May 1, 2026", he: "1 במאי 2026" } },
    ],
  },

  communities: [
    {
      id: 1,
      name: { en: "Neve Sha'anan Towers", he: "מגדלי נווה שאנן" },
      image: "/mysupplier.png",
      status: "active",
      needsAttention: false,
      cm: { name: { en: "Shoshana Levi", he: "שושנה לוי" }, avatar: "/avataar.png" },
      members: 128,
      suppliers: 34,
      transactions: 412,
      items: { services: 46, products: 22, foods: 9 },
      incomeLastMonth: 32100,
      cutPercent: 15,
    },
    {
      id: 2,
      name: { en: "Park HaYarkon Residences", he: "מעונות פארק הירקון" },
      image: "/supplier-icon.png",
      status: "active",
      needsAttention: false,
      cm: { name: { en: "Nati Cohen", he: "נתי כהן" }, avatar: "/usravatar.png" },
      members: 96,
      suppliers: 21,
      transactions: 268,
      items: { services: 30, products: 14, foods: 0 },
      incomeLastMonth: 24800,
      cutPercent: 20,
    },
    {
      id: 3,
      name: { en: "Kfar Sava Green", he: "כפר סבא ירוקה" },
      image: "/people.png",
      status: "active",
      needsAttention: true,
      cm: { name: { en: "David Mizrahi", he: "דוד מזרחי" }, avatar: "/profile.png" },
      members: 54,
      suppliers: 11,
      transactions: 97,
      items: { services: 12, products: 5, foods: 3 },
      incomeLastMonth: 9800,
      cutPercent: 15,
    },
    {
      id: 4,
      name: { en: "Ramat HaChayal Hub", he: "רמת החייל האב" },
      image: "/avataar.png",
      status: "active",
      needsAttention: false,
      cm: { name: { en: "You (self-managed)", he: "אתה (ניהול עצמי)" }, avatar: "/profile.png" },
      selfManaged: true, // the MoM is also this community's CM → can switch into managing it
      members: 142,
      suppliers: 41,
      transactions: 503,
      items: { services: 52, products: 31, foods: 18 },
      incomeLastMonth: 41200,
      cutPercent: 15,
    },
  ],
};

// Existing platform users the MoM can search to assign as a CM (mock).
export const searchableUsers = [
  { id: "u1", name: { en: "Maya Bar", he: "מאיה בר" }, subtitle: "maya.bar@email.com", avatar: "/avataar.png" },
  { id: "u2", name: { en: "Yossi Meir", he: "יוסי מאיר" }, subtitle: "yossi.meir@email.com", avatar: "/usravatar.png" },
  { id: "u3", name: { en: "Tal Benda", he: "טל בנדה" }, subtitle: "052-1234567", avatar: "/profile.png" },
  { id: "u4", name: { en: "Noa Katz", he: "נועה כץ" }, subtitle: "noa.katz@email.com", avatar: "/people.png" },
  { id: "u5", name: { en: "Amir Levi", he: "אמיר לוי" }, subtitle: "054-7654321", avatar: "/avataar.png" },
];

// Portfolio totals for the summary row. myCut is derived per community from the
// (illustrative) cut % — never stored, so editing a cut updates the total.
export const getTotals = (m, cutOf) => {
  const c = m.communities;
  const items = {};
  ITEM_TYPES.forEach((tp) => {
    items[tp] = c.reduce((s, x) => s + (x.items?.[tp] || 0), 0);
  });
  return {
    communities: c.length,
    members: c.reduce((s, x) => s + x.members, 0),
    suppliers: c.reduce((s, x) => s + x.suppliers, 0),
    transactions: c.reduce((s, x) => s + x.transactions, 0),
    items,
    incomeLastMonth: c.reduce((s, x) => s + x.incomeLastMonth, 0),
    myCut: c.reduce((s, x) => s + Math.round((x.incomeLastMonth * cutOf(x)) / 100), 0),
  };
};
